import { useLiveQuery } from 'dexie-react-hooks';
import { DEFAULT_SPEED } from '@familyhub/shared';
import { mdb } from './db';

/**
 * How lessons are played. Per phone, not synced: whether this phone can buzz,
 * and how loud the room is, are facts about the phone. Kept in the module's own
 * Dexie meta table — nothing in this app lives in localStorage.
 */
export interface MorseSettings {
  sound: boolean;
  flash: boolean;
  vibrate: boolean;
  charWpm: number;
  effectiveWpm: number;
  toneHz: number;
}

/**
 * What is best for learning by ear, and so also the default: sound only, letters
 * at full speed (20 wpm) with the gaps stretched to 10. Vibration is off on
 * purpose — it slows the letters down to be felt, which teaches a count rather
 * than a sound.
 */
export const DEFAULT_SETTINGS: MorseSettings = {
  sound: true,
  flash: false,
  vibrate: false,
  charWpm: DEFAULT_SPEED.charWpm,
  effectiveWpm: DEFAULT_SPEED.effectiveWpm,
  toneHz: 650,
};

export function useMorseSettings(): MorseSettings {
  const row = useLiveQuery(() => mdb.meta.get('settings'), []);
  return { ...DEFAULT_SETTINGS, ...((row?.value as Partial<MorseSettings> | undefined) ?? {}) };
}

export async function saveMorseSettings(next: MorseSettings): Promise<void> {
  await mdb.meta.put({ key: 'settings', value: next });
}

/**
 * The two recommended modes (household request, 2026-10-14). The flashing part
 * of the screen is not an extra beside the tone — it is the way to practise where
 * sound cannot be used, so it gets its own preset.
 *
 * - `sound`: learning by ear, the default above.
 * - `silent`: the lamp alone, slower. An eye resolves a flash far worse than an
 *   ear resolves a tone: at 20 wpm a dot is 60 ms, about four frames, which reads
 *   as a flicker rather than a short. 12 wpm makes it 100 ms with dashes at 300,
 *   clearly two lengths, and the gaps stretched to 6 keep the overall pace calm.
 */
export const RECOMMENDED = {
  sound: DEFAULT_SETTINGS,
  silent: { sound: false, flash: true, vibrate: false, charWpm: 12, effectiveWpm: 6, toneHz: DEFAULT_SETTINGS.toneHz },
} satisfies Record<string, MorseSettings>;

export type RecommendedMode = keyof typeof RECOMMENDED;

/** Which recommended mode these settings are exactly, if any. */
export function recommendedMode(s: MorseSettings): RecommendedMode | null {
  for (const mode of Object.keys(RECOMMENDED) as RecommendedMode[]) {
    const r = RECOMMENDED[mode];
    if ((Object.keys(r) as (keyof MorseSettings)[]).every((k) => s[k] === r[k])) return mode;
  }
  return null;
}

export const canVibrate = (): boolean =>
  typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function';
